import React from 'react'
import { List } from 'immutable'
import { configurationsService } from 'shared/services/ConfigurationsService'
import { LibraryView } from 'components/views/LibraryView'
import { useBreakpoint } from '../store/navigation/hooks/useBreakpoint'
import { useLanguage } from '../store/locale/hooks/useLanguage'
import { library } from '../data/media/library'
import { useTranslations } from './useTranslations'
import { TextService } from 'shared/services/TextService'
import { BackgroundPositionEnum } from 'components/ui/DataDisplay/Image'
import { useStaticSWR } from 'shared/hooks/useStaticSWR'

export enum ELanguage {
  fr = 'fr',
  en = 'en'
}

export interface ILibraryItem {
  title: Record<ELanguage, string>
  url: string
  thumbnail: string
  isVideo: boolean
  backgroundPosition: BackgroundPositionEnum
}

export interface ILibraryFolder {
  id: number
  title: Record<ELanguage, string>
  subtitle: string
  parent: number
  childFolders: ILibraryFolder[]
  thumbnail: string
  url: string
  items: ILibraryItem[]
}

const LibraryBaseUrl = 'media/mediatheque'

const backgroundPositionConverter = (backgroundPositionDisplay: string): BackgroundPositionEnum => {
  switch (backgroundPositionDisplay) {
    case 'Haut':
      return BackgroundPositionEnum.TOP
    case 'Centre':
      return BackgroundPositionEnum.CENTER
    case 'Bas':
      return BackgroundPositionEnum.BOTTOM
    default:
      return BackgroundPositionEnum.CENTER
  }
}

const extractFolders = (data: any, language: string): List<ILibraryFolder> => {
  const languageSuffix = language !== 'fr' ? `_${language}` : ''

  const orderItems = (items) => {
    return items.sort((a, b) => a[`title${languageSuffix}`].localeCompare(b[`title${languageSuffix}`]))
  }

  return List<ILibraryFolder>(
    data
      ?.filter((x) => x[`title${languageSuffix}`])
      .map((folder) => ({
        id: folder.id,
        title: { en: folder.title_en, fr: folder.title },
        root: folder.root,
        parent: folder.parent_folder?.id,
        childFolders: folder.child_folders
          .filter((x) => x[`title${languageSuffix}`])
          .map((child) => ({
            id: child.id,
            title: { en: child.title_en, fr: child.title },
            subtitle: { en: child.subtitle_en, fr: child.subtitle },
            thumbnail: configurationsService.imageBaseUrl + child.thumbnail?.url
          })),
        items: folder.items
          ? orderItems(folder.items)
              .filter((x) => x[`title${languageSuffix}`])
              .map((item) => {
                return {
                  title: { en: item.title_en, fr: item.title },
                  url: configurationsService.imageBaseUrl + item.media?.url,
                  thumbnail: configurationsService.imageBaseUrl + item.thumbnail?.url,
                  isVideo: item.media?.mime?.indexOf('video') !== -1,
                  backgroundPosition: backgroundPositionConverter(item.backgroundPosition)
                }
              })
          : []
      }))
  )
}

const buildLibraryRoutes = (
  baseURL: string,
  folder: ILibraryFolder,
  folders: List<ILibraryFolder>,
  isRootFolder: boolean = true
): { [key: string]: () => any } => {
  let routeMap: { [key: string]: () => any } = {}

  Object.keys(ELanguage).map((lang) => {
    const folderURL = `${baseURL}${isRootFolder ? '' : `/${TextService.routifyName(folder?.title[lang])}`}`
    routeMap[folderURL] = () => <LibraryView {...folder} />

    const childrenMaps = folder.childFolders.map((childFolderRef) => {
      const childFolder = folders.find((folder: ILibraryFolder) => childFolderRef.id === folder.id)
      return childFolder ? buildLibraryRoutes(folderURL, childFolder, folders, false) : {}
    })

    return childrenMaps.forEach((childMap) => {
      routeMap = { ...routeMap, ...childMap }
    })
  })
  return routeMap
}

export const useLibrary = () => {
  const { language } = useLanguage()
  const breakpoint = useBreakpoint()
  const t = useTranslations()
  const { data } = useStaticSWR({ key: configurationsService.libraryFolderEndpoint })

  const folders = extractFolders(data, language)

  const rootFolder = folders.find((folder) => folder.title[language] === 'Root')
  const libraryRoutes = rootFolder ? buildLibraryRoutes(LibraryBaseUrl, rootFolder, folders) : null

  const staticData = library({ language, breakpoint })

  return {
    libraryRoutes,
    isLoaded: libraryRoutes && Object.keys(libraryRoutes).length > 0,
    staticData,
    t,
    isLoading: !data
  }
}
